import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { t3, pick3 } from "../i18n/v3";
import PageBackdrop from "../components/PageBackdrop";

const ease = [0.16, 1, 0.3, 1];

/* i18n copy specific to the new hero and marginalia. */
const HERO = {
    eyebrow: {
        en: "Life is a Project", es: "Life is a Project", nl: "Life is a Project", fr: "Life is a Project",
    },
    wordmark: {
        en: "Life is a Project", es: "Life is a Project", nl: "Life is a Project", fr: "Life is a Project",
    },
    tagline: {
        en: "A Brighter Tomorrow Through Vision, Ideas and Action",
        es: "Un mañana más luminoso a través de la visión, las ideas y la acción",
        nl: "Een helderder morgen door visie, ideeën en actie",
        fr: "Un avenir plus clair par la vision, les idées et l’action",
    },
    triad: {
        en: ["Ideas", "Research", "Projects"],
        es: ["Ideas", "Investigación", "Proyectos"],
        nl: ["Ideeën", "Onderzoek", "Projecten"],
        fr: ["Idées", "Recherche", "Projets"],
    },
    marginalia: {
        en: ["Ideas illuminate", "the path.", "Action builds", "what comes next."],
        es: ["Las ideas iluminan", "el camino.", "La acción construye", "lo que viene."],
        nl: ["Ideeën verlichten", "de weg.", "Actie bouwt", "wat komt."],
        fr: ["Les idées éclairent", "le chemin.", "L’action bâtit", "ce qui suit."],
    },
};

/* -------------------------------------------------------------------------- */
/*  Hero — the background composition IS the hero. It already contains the    */
/*  wordmark, tagline and IDEAS·RESEARCH·PROJECTS triad — so no HTML text is  */
/*  layered on top. A visually-hidden <h1> preserves the semantic page title  */
/*  for screen readers and SEO without duplicating what the image conveys.    */
/* -------------------------------------------------------------------------- */
function Hero() {
    const { lang } = useLanguage();
    return (
        <section
            aria-labelledby="home-hero-heading"
            className="relative w-full h-[70svh] md:h-auto md:aspect-[1526/602] md:max-h-[92svh] overflow-hidden"
        >
            <PageBackdrop variant="home" testId="hero-backdrop" />
            <h1
                id="home-hero-heading"
                data-testid="hero-heading"
                className="sr-only"
            >
                {pick3(HERO.wordmark, lang)} — {pick3(HERO.tagline, lang)}
            </h1>
        </section>
    );
}

/* -------------------------------------------------------------------------- */
/*  What is Life is a Project? — concise overview of the four surfaces.       */
/* -------------------------------------------------------------------------- */
function WhatSection() {
    const { lang } = useLanguage();
    const areas = pick3(t3.home.what.areas, lang);
    return (
        <section
            aria-labelledby="home-what-heading"
            className="relative bg-[color:var(--ivory)] border-t border-[color:var(--line)]"
        >
            <div className="max-w-[1120px] mx-auto px-6 md:px-10 py-20 md:py-28">
                <div className="grid grid-cols-12 gap-6 mb-12">
                    <div className="col-span-12 md:col-span-4">
                        <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-4">01 · Overview</div>
                        <h2
                            id="home-what-heading"
                            className="font-display font-light tracking-[-0.01em] leading-[1.08] text-[color:var(--navy)]"
                            style={{ fontSize: "clamp(28px, 3.4vw, 44px)" }}
                        >
                            {pick3(t3.home.what.title, lang)}
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-7 md:col-start-6 flex items-end">
                        <p className="text-[17px] md:text-[19px] leading-[1.6] text-[color:var(--navy)]/85 max-w-[56ch]">
                            {pick3(t3.home.what.body, lang)}
                        </p>
                    </div>
                </div>

                <div className="mt-10 border-t border-[color:var(--line-strong)]">
                    {areas.map((a, i) => (
                        <motion.div
                            key={a.key}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-8%" }}
                            transition={{ duration: 0.7, ease, delay: i * 0.05 }}
                            className="border-b border-[color:var(--line)]"
                        >
                            <Link
                                to={a.route}
                                data-testid={`area-${a.key}`}
                                className="grid grid-cols-12 gap-6 items-baseline py-7 group"
                            >
                                <div className="col-span-2 md:col-span-1 font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <div className="col-span-10 md:col-span-4">
                                    <h3 className="font-display text-[24px] md:text-[30px] leading-[1.05] text-[color:var(--navy)] group-hover:italic transition-all duration-500">{a.t}</h3>
                                </div>
                                <div className="col-span-12 md:col-span-6 md:col-start-7 flex items-center gap-6">
                                    <p className="text-[15px] md:text-[16px] leading-[1.6] text-[color:var(--navy)]/80 flex-1">{a.d}</p>
                                    <span aria-hidden className="w-8 h-px bg-[color:var(--gold)] transition-all duration-500 group-hover:w-14" />
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* -------------------------------------------------------------------------- */
/*  Philosophy teaser — short link to /philosophy.                            */
/* -------------------------------------------------------------------------- */
function PhilosophyTeaser() {
    const { lang } = useLanguage();
    return (
        <section
            aria-labelledby="home-philosophy-heading"
            className="relative bg-[color:var(--ivory)] border-t border-[color:var(--line)]"
        >
            <div className="max-w-[1120px] mx-auto px-6 md:px-10 py-20 md:py-28">
                <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-12 md:col-span-4">
                        <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-4">02 · Philosophy</div>
                        <h2
                            id="home-philosophy-heading"
                            className="font-display italic font-light tracking-[-0.01em] leading-[1.1] text-[color:var(--navy)]"
                            style={{ fontSize: "clamp(24px, 2.6vw, 32px)" }}
                        >
                            {pick3(t3.home.philosophy.overline, lang)}
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-7 md:col-start-6">
                        <p className="font-display text-[28px] md:text-[40px] leading-[1.12] tracking-[-0.01em] text-[color:var(--navy)] max-w-[26ch]">
                            {pick3(t3.home.philosophy.line1, lang)}
                        </p>
                        <p className="mt-6 max-w-[58ch] text-[16px] md:text-[17px] leading-[1.7] text-[color:var(--navy)]/85">
                            {pick3(t3.home.philosophy.line2, lang)}
                        </p>
                        <Link
                            to="/philosophy"
                            data-testid="home-philosophy-link"
                            className="mt-10 inline-flex items-center gap-3 link-editorial font-display italic text-[18px] text-[color:var(--navy)] group"
                        >
                            <span>{pick3(t3.home.philosophy.link, lang)}</span>
                            <span aria-hidden className="w-6 h-px bg-[color:var(--gold)] transition-all duration-500 group-hover:w-12" />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function HomeV3() {
    return (
        <main
            id="main"
            tabIndex={-1}
            data-testid="page-home"
            className="focus:outline-none pt-16 lg:pt-0 min-h-screen"
        >
            <div className="lg:pl-[var(--sidebar-w)]">
                <Hero />
                <WhatSection />
                <PhilosophyTeaser />
            </div>
        </main>
    );
}
